import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  FaRocket, 
  FaUsers, 
  FaGlobe, 
  FaShieldAlt, 
  FaBolt,
  FaArrowRight,
  FaChartLine,
  FaCode,
  FaBrain,
  FaEye,
  FaHeart,
  FaTerminal,
  FaSearch,
  FaMicrochip,
  FaWindows,
  FaLinux,
  FaApple,
  FaStar,
  FaLayerGroup,
  FaCog,
  FaLightbulb,
  FaDownload
} from "react-icons/fa";
import Logo from "../components/Logo";
import Footer from "../components/Footer";
import backgroundVideo from "../assets/hero-background.mp4";
import "../styles/OurVision.css";

function OurVision() {
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [activeTimelineItem, setActiveTimelineItem] = useState(0);
  const timelineRef = useRef(null);

  // Handle scroll for header transparency
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      
      // Get the actual hero section element
      const heroSection = document.querySelector('.vision-hero');
      
      if (heroSection) {
        // Get the bottom of the hero section
        const heroBottom = heroSection.offsetHeight - 100;
        setIsHeaderScrolled(scrollPosition > heroBottom);
      } else {
        setIsHeaderScrolled(scrollPosition > window.innerHeight * 0.9);
      }
    };

    window.addEventListener('scroll', handleScroll);
    const timer = setTimeout(handleScroll, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  // Timeline auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTimelineItem(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const timelineData = [
    { year: "2025", title: "Foundation Platform", description: "Core AI-powered endpoint research capabilities" },
    { year: "2026", title: "Advanced AI Models", description: "Domain-specific intelligence for security" },
    { year: "2027", title: "Integrated Security Research", description: "Complete unified research ecosystem" }
  ];

  return (
    <div className="vision-container">
      {/* Ambient Background */}
      <div className="vision-ambient-bg">
        <div className="ambient-orb ambient-orb-1"></div>
        <div className="ambient-orb ambient-orb-2"></div>
        <div className="ambient-orb ambient-orb-3"></div>
      </div>
      
      <video autoPlay loop muted playsInline className="vision-background-video">
        <source src={backgroundVideo} type="video/mp4" />
      </video>
      <div className="vision-overlay"></div>
      
      <header className={`vision-header ${isHeaderScrolled ? 'scrolled' : ''}`}>
        <Logo size="small" showText={false} linkTo="/" />
        <nav className="nav-links">
          <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
          <Link to="/login" className="login-link">Login</Link>
          <Link to="/register" className="register-link">Register</Link>
        </nav>
      </header>

      <main className="vision-main">
        {/* Hero Section */}
        <section className="vision-hero">
          <div className="hero-decoration">
            <div className="deco-line deco-line-1"></div>
            <div className="deco-line deco-line-2"></div>
            <div className="deco-circle deco-circle-1"></div>
            <div className="deco-circle deco-circle-2"></div>
          </div>
          
          <div className="hero-content">
            <div className="hero-eyebrow">
              <span className="eyebrow-dot"></span>
              Our Vision
            </div>
            <h1>
              <span className="hero-title-line">Revolutionizing</span>
              <span className="hero-title-gradient">Endpoint Security Research</span>
              <span className="hero-title-accent">with AI</span>
            </h1>
            <p className="hero-description">
              At triagz, our mission is to revolutionize endpoint security research by making even the most 
              complex tasks seamless and intuitive with the power of AI. We've ushered in an era that moves beyond memorizing commands,
              managing multiple tools, or hoping between multiple paid platforms.
            </p>
            <p className="hero-highlight">
              <FaLightbulb className="highlight-icon" />
              With a single prompt, security researchers can instantly perform tasks across multiple platforms in a centralized space, in real time.
            </p>
          </div>
          
          <div className="hero-visual">
            <div className="visual-container">
              <div className="visual-ring visual-ring-outer">
                <div className="ring-particle ring-particle-1"></div>
                <div className="ring-particle ring-particle-2"></div>
                <div className="ring-particle ring-particle-3"></div>
              </div>
              <div className="visual-ring visual-ring-middle"></div>
              <div className="visual-ring visual-ring-inner"></div>
              <div className="visual-core">
                <FaBrain className="core-icon" />
              </div>
              <div className="floating-elements">
                <div className="float-card float-card-1">
                  <FaTerminal />
                  <span>Single Prompt</span>
                </div>
                <div className="float-card float-card-2">
                  <FaShieldAlt />
                  <span>Multi-Platform</span>
                </div>
                <div className="float-card float-card-3">
                  <FaBolt />
                  <span>Real-Time</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="mission-section">
          <div className="section-glow section-glow-left"></div>
          <div className="container">
            <div className="mission-layout">
              <div className="mission-content">
                <div className="section-label">
                  <span className="label-line"></span>
                  Our Mission
                  <span className="label-line"></span>
                </div>
                <h2>Eliminating the Friction of Traditional Toolchains</h2>
                <p className="mission-text">
                  We envision a future where the friction of traditional toolchains is eliminated. Imagine 
                  querying your firmware, extracting TTPs, or decrypting intricate logic in one go—no lengthy 
                  setup, no steep learning curve. This platform will become the go-to hub for everyone in 
                  security research, malware analysis, incident response, and system administration.
                </p>
                <div className="mission-metrics">
                  <div className="metric-card">
                    <div className="metric-value">3</div>
                    <div className="metric-label">Operating Systems</div>
                    <div className="metric-icons">
                      <FaWindows />
                      <FaLinux />
                      <FaApple />
                    </div>
                  </div>
                  <div className="metric-card metric-card-highlight">
                    <div className="metric-value">1</div>
                    <div className="metric-label">Unified Prompt</div>
                    <div className="metric-visual">
                      <span className="prompt-cursor"></span>
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">∞</div>
                    <div className="metric-label">Possibilities</div>
                    <div className="metric-stars">
                      <FaStar />
                      <FaStar />
                      <FaStar />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mission-visual">
                <div className="globe-container">
                  <div className="globe-core">
                    <FaGlobe />
                  </div>
                  <div className="orbit-ring orbit-1">
                    <div className="orbit-node"></div>
                  </div>
                  <div className="orbit-ring orbit-2">
                    <div className="orbit-node"></div>
                  </div>
                  <div className="orbit-ring orbit-3">
                    <div className="orbit-node"></div>
                  </div>
                  <div className="connection-lines">
                    <div className="conn-line conn-line-1"></div>
                    <div className="conn-line conn-line-2"></div>
                    <div className="conn-line conn-line-3"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="values-section">
          <div className="container">
            <div className="section-header-centered">
              <div className="section-label">
                <span className="label-line"></span>
                Core Principles
                <span className="label-line"></span>
              </div>
              <h2>What Drives Us</h2>
              <p className="section-description">The principles that guide every decision and innovation</p>
            </div>
            
            <div className="values-grid">
              <div className="value-card value-card-featured">
                <div className="value-card-glow"></div>
                <div className="value-icon-container">
                  <div className="value-icon">
                    <FaTerminal />
                  </div>
                  <div className="icon-pulse"></div>
                </div>
                <h3>Seamless Integration</h3>
                <p>
                  Single prompt functionality for file inspection, process exploration, 
                  log extraction, reverse engineering and more—all in real time.
                </p>
                <div className="value-footer">
                  <span className="value-tag">Core Feature</span>
                </div>
              </div>
              
              <div className="value-card">
                <div className="value-card-glow"></div>
                <div className="value-icon-container">
                  <div className="value-icon">
                    <FaRocket />
                  </div>
                </div>
                <h3>Innovation First</h3>
                <p>
                  We're eliminating the complexity of traditional security toolchains, 
                  making advanced analysis accessible to specialists and newcomers alike.
                </p>
              </div>
              
              <div className="value-card">
                <div className="value-card-glow"></div>
                <div className="value-icon-container">
                  <div className="value-icon">
                    <FaUsers />
                  </div>
                </div>
                <h3>Community Powered</h3>
                <p>
                  Empowering security researchers, incident responders, and system 
                  administrators to focus on insights rather than infrastructure.
                </p>
              </div>
              
              <div className="value-card">
                <div className="value-card-glow"></div>
                <div className="value-icon-container">
                  <div className="value-icon">
                    <FaBolt />
                  </div>
                </div>
                <h3>Real-Time Analysis</h3>
                <p>
                  Lightning-fast analysis across any connected machine, whether it's 
                  Windows, Linux, or macOS—with just a single agent installation.
                </p>
              </div>
              
              <div className="value-card">
                <div className="value-card-glow"></div>
                <div className="value-icon-container">
                  <div className="value-icon">
                    <FaMicrochip />
                  </div>
                </div>
                <h3>Deep Intelligence</h3>
                <p>
                  From firmware queries to TTP extraction, our AI understands the 
                  intricacies of endpoint security research.
                </p>
              </div>
              
              <div className="value-card">
                <div className="value-card-glow"></div>
                <div className="value-icon-container">
                  <div className="value-icon">
                    <FaGlobe />
                  </div>
                </div>
                <h3>Universal Platform</h3>
                <p>
                  One platform will support your favorite 3rd party security products and MCPs to escalate your research.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Future Vision */}
        <section className="future-section">
          <div className="section-glow section-glow-right"></div>
          <div className="container">
            <div className="future-layout">
              <div className="future-content">
                <div className="section-label section-label-left">
                  <span className="label-line"></span>
                  Looking Ahead
                </div>
                <h2>The Future We're Building</h2>
                <p className="future-intro">
                  In the years to come, our AI will grow in capability and intelligence. Routine tasks 
                  will give way to advanced analysis: automated threat hunting, deep malware reverse 
                  engineering, live incident triage, and even proactive vulnerability assessments—all 
                  triggered by a single, natural-language prompt.
                </p>
                
                <div className="future-capabilities">
                  <div className="capability-item">
                    <div className="capability-icon">
                      <FaBrain />
                    </div>
                    <div className="capability-content">
                      <h4>Automated Threat Hunting</h4>
                      <p>AI agents that continuously hunt for emerging threats and perform deep malware analysis</p>
                    </div>
                    <div className="capability-line"></div>
                  </div>
                  
                  <div className="capability-item">
                    <div className="capability-icon">
                      <FaSearch />
                    </div>
                    <div className="capability-content">
                      <h4>Live Incident Triage</h4>
                      <p>Real-time incident response and analysis through natural language prompts</p>
                    </div>
                    <div className="capability-line"></div>
                  </div>
                  
                  <div className="capability-item">
                    <div className="capability-icon">
                      <FaShieldAlt />
                    </div>
                    <div className="capability-content">
                      <h4>Endpoint Security Assessment</h4>
                      <p>Continuous security assessment including vulnerability analysis, debugging and more</p>
                    </div>
                    <div className="capability-line"></div>
                  </div>
                </div>
              </div>
              
              <div className="future-visual" ref={timelineRef}>
                <div className="timeline-container">
                  <div className="timeline-track">
                    <div 
                      className="timeline-progress" 
                      style={{ height: `${((activeTimelineItem + 1) / 3) * 100}%` }}
                    ></div>
                  </div>
                  {timelineData.map((item, index) => (
                    <div 
                      key={index}
                      className={`timeline-entry ${activeTimelineItem >= index ? 'active' : ''} ${activeTimelineItem === index ? 'current' : ''}`}
                      onClick={() => setActiveTimelineItem(index)}
                    >
                      <div className="timeline-marker">
                        <div className="marker-dot"></div>
                        <div className="marker-ring"></div>
                      </div>
                      <div className="timeline-card">
                        <span className="timeline-year">{item.year}</span>
                        <h4 className="timeline-title">{item.title}</h4>
                        <p className="timeline-desc">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Future Enhancements */}
        <section className="enhancements-section">
          <div className="container">
            <div className="section-header-centered">
              <div className="section-label">
                <span className="label-line"></span>
                Coming Soon
                <span className="label-line"></span>
              </div>
              <h2>Future Enhancements</h2>
            </div>
            
            <div className="enhancements-grid">
              <div className="enhancement-card">
                <div className="enhancement-number">01</div>
                <div className="enhancement-icon">
                  <FaLayerGroup />
                </div>
                <h3>Unified Endpoint Research Environment</h3>
                <p>We're integrating a wide range of Management Control Protocols (MCPs) and 3rd party security products directly into the platform. No extra environment setup—connect once and manage everywhere.</p>
                <div className="enhancement-glow"></div>
              </div>
              
              <div className="enhancement-card">
                <div className="enhancement-number">02</div>
                <div className="enhancement-icon">
                  <FaCog />
                </div>
                <h3>Tailored AI Intelligence</h3>
                <p>Our fine-tuned, domain-specific AI models will deliver unmatched accuracy and relevance, outperforming any generic LLM for endpoint security tasks.</p>
                <div className="enhancement-glow"></div>
              </div>
              
              <div className="enhancement-card">
                <div className="enhancement-number">03</div>
                <div className="enhancement-icon">
                  <FaCode />
                </div>
                <h3>Expanding Functionality</h3>
                <p>From custom script execution to interactive visualizations, you'll see new capabilities added continuously—because your analysis should never be limited by the tool.</p>
                <div className="enhancement-glow"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="vision-cta">
          <div className="cta-background">
            <div className="cta-grid-lines"></div>
            <div className="cta-glow cta-glow-1"></div>
            <div className="cta-glow cta-glow-2"></div>
          </div>
          <div className="container">
            <div className="cta-layout">
              <div className="cta-content">
                <h2>Join the Security Research Revolution</h2>
                <p>
                  Experience the future of endpoint security research today. Whether you're analyzing malware, 
                  responding to incidents, or conducting system administration, triagz empowers you to work 
                  smarter, not harder.
                </p>
                <div className="cta-buttons">
                  <Link to="/register" className="btn btn-primary btn-glow">
                    Get Started <FaArrowRight />
                  </Link>
                  <Link to="/career" className="btn btn-secondary">
                    Join Our Team
                  </Link>
                </div>
              </div>
              <div className="cta-visual">
                <div className="network-visualization">
                  <div className="network-node node-center">
                    <FaShieldAlt />
                  </div>
                  <div className="network-node node-1"><FaTerminal /></div>
                  <div className="network-node node-2"><FaSearch /></div>
                  <div className="network-node node-3"><FaBrain /></div>
                  <div className="network-node node-4"><FaCode /></div>
                  <svg className="network-lines" viewBox="0 0 300 300">
                    <line x1="150" y1="150" x2="75" y2="75" className="network-line" />
                    <line x1="150" y1="150" x2="225" y2="75" className="network-line" />
                    <line x1="150" y1="150" x2="75" y2="225" className="network-line" />
                    <line x1="150" y1="150" x2="225" y2="225" className="network-line" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default OurVision;